import { jsx as _jsx } from "react/jsx-runtime";
import React, { Component } from 'react';
import { ErrorFallback } from './ErrorFallback.js';
/**
 * ErrorBoundary - A React error boundary component that catches JavaScript errors
 * anywhere in the child component tree and displays a fallback UI.
 *
 * This component prevents the entire app from crashing when an error occurs
 * in a child component. Instead, it displays a user-friendly error message
 * and provides options to recover.
 *
 * Features:
 * - Catches rendering errors in child components
 * - Customizable fallback UI
 * - Error logging callback for analytics/monitoring
 * - Reset functionality to retry rendering
 * - TypeScript support with strict typing
 *
 * @example
 * ```tsx
 * // Basic usage
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 *
 * // With custom error handling
 * <ErrorBoundary
 *   onError={(error, errorInfo) => {
 *     // Log to error tracking service
 *     logErrorToService(error, errorInfo);
 *   }}
 *   fallbackTitle="Oops! Something went wrong"
 *   fallbackDescription="Please refresh the page to try again."
 * >
 *   <App />
 * </ErrorBoundary>
 *
 * // With custom fallback component
 * <ErrorBoundary FallbackComponent={MyCustomErrorPage}>
 *   <App />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }
    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true, error };
    }
    componentDidCatch(error, errorInfo) {
        // Log error to console in development
        if (process.env['NODE_ENV'] === 'development') {
            console.error('ErrorBoundary caught an error:', error);
            console.error('Component stack:', errorInfo.componentStack);
        }
        // Call the onError callback if provided
        this.props.onError?.(error, errorInfo);
    }
    resetErrorBoundary = () => {
        this.props.onReset?.();
        this.setState({ hasError: false, error: null });
    };
    render() {
        const { children, FallbackComponent, fallbackTitle, fallbackDescription, showErrorDetails, } = this.props;
        const { hasError, error } = this.state;
        if (hasError && error) {
            // Use custom fallback component if provided
            if (FallbackComponent) {
                return (_jsx(FallbackComponent, { error: error, resetErrorBoundary: this.resetErrorBoundary, title: fallbackTitle, description: fallbackDescription, showErrorDetails: showErrorDetails }));
            }
            // Use default ErrorFallback component
            return (_jsx(ErrorFallback, { error: error, resetErrorBoundary: this.resetErrorBoundary, title: fallbackTitle, description: fallbackDescription, showErrorDetails: showErrorDetails }));
        }
        return children;
    }
}
//# sourceMappingURL=ErrorBoundary.js.map